"use client";
import { Table, Modal, Popconfirm, Button, Space as BtnSpace, Flex, theme } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { TableProps, ColumnType } from "antd/es/table";
import { useEffect, useReducer, useRef, useState } from "react";
import React from "react";
import { AppModal } from "./AppModal";
import { toPascalCase } from "@/lib/format";

// -- column types --------------------------------------------------------------

export interface GridColumn extends Omit<ColumnType<Record<string, unknown>>, "dataIndex" | "title" | "key"> {
  name: string;
  label?: string;
}

// -- action types --------------------------------------------------------------

export type ActionButtonType = "link" | "text" | "default" | "primary" | "dashed";
export type FetchMethod = "GET" | "POST";

export interface GridAction {
  key: string;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  buttonType?: ActionButtonType;
  danger?: boolean;
  /**
   * Wraps button in Popconfirm before firing onConfirm.
   * Use for destructive actions like delete.
   */
  confirm?: {
    title: string;
    description?: string;
    okText?: string;
    cancelText?: string;
    onConfirm: (record: Record<string, unknown>) => void | Promise<void>;
  };
  /**
   * Opens a Modal when button is clicked.
   * content(record, close) renders the modal body.
   */
  modal?: {
    title?: string | ((record: Record<string, unknown>) => string);
    width?: number | string;
    content: (record: Record<string, unknown>, close: () => void) => React.ReactNode;
  };
  /** Plain click handler (used when no confirm/modal). */
  onClick?: (record: Record<string, unknown>) => void;
}

// -- grid props ----------------------------------------------------------------

type AppGridProps = Omit<TableProps<Record<string, unknown>>, "columns" | "dataSource" | "pagination"> & {
  columns?: GridColumn[];
  dataSource?: Record<string, unknown>[];
  url?: string;
  /**
   * Server-action data source. Preferred over `url` — man-restro loads data
   * through actions, not client fetch. Given `{ page, pageSize }` (use them only
   * when `serverPage`); return either a plain row array, or `{ data, total }`
   * for server pagination.
   */
  loader?: (
    params: { page: number; pageSize: number }
  ) => Promise<any[] | { data: any[]; total?: number }>;
  child?: (AppGridProps & { dataSourceKey?: string });
  method?: FetchMethod;
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  actions?: GridAction[];
  actionsTitle?: string;
  actionsWidth?: number;
  actionsPosition?: "start" | "end";
  /** Enable server-side pagination. When true, expects API to handle page/pageSize params. */
  serverPage?: boolean;
  /** Page size for pagination. Defaults to 10. */
  pageSize?: number;
  /** Auto-expand all rows with expandable content. */
  autoExpand?: boolean;
  /** Optional "Add" button rendered at the top-right of the grid. */
  addButton?: {
    /** Button label. Defaults to "Add +" */
    label?: React.ReactNode;
    icon?: React.ReactNode;
    /** Modal opened when button is clicked. */
    modal?: {
      title?: string;
      width?: number | string;
      content: (close: () => void) => React.ReactNode;
    };
    /** Plain click handler (used when no modal is provided). */
    onClick?: () => void;
  };
};

// -- helpers -------------------------------------------------------------------

function buildColumns(
  colDefs: GridColumn[] | undefined,
  firstRow: Record<string, unknown> | undefined
): ColumnType<Record<string, unknown>>[] {
  const defs: GridColumn[] =
    colDefs && colDefs.length > 0
      ? colDefs
      : firstRow
        ? Object.keys(firstRow).map((k) => ({ name: k }))
        : [];

  return defs.map(({ name, label, ...rest }) => ({
    key: name,
    dataIndex: name,
    title: label ?? toPascalCase(name),
    ...rest,
  }));
}

function extractArray(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    for (const key of ["data", "items", "rows", "results"]) {
      if (Array.isArray(obj[key])) return obj[key] as Record<string, unknown>[];
    }
  }
  return [];
}

// -- fetch reducer (with pagination) -------------------------------------------

type FetchState = {
  data: Record<string, unknown>[];
  loading: boolean;
  error: string | null;
  total?: number;
};

type FetchAction =
  | { type: "LOADING" }
  | { type: "SUCCESS"; payload: Record<string, unknown>[]; total?: number }
  | { type: "ERROR"; payload: string };

function fetchReducer(state: FetchState, action: FetchAction): FetchState {
  switch (action.type) {
    case "LOADING":
      return { ...state, loading: true, error: null };
    case "SUCCESS":
      return { data: action.payload, loading: false, error: null, total: action.total };
    case "ERROR":
      return { ...state, loading: false, error: action.payload };
  }
}

// -- action cell ---------------------------------------------------------------

type ModalState = {
  action: GridAction;
  record: Record<string, unknown>;
};

function ActionCell({
  actions,
  record,
  onOpenModal,
}: {
  actions: GridAction[];
  record: Record<string, unknown>;
  onOpenModal: (state: ModalState) => void;
}) {
  return (
    <BtnSpace size="small">
      {actions.map((action) => {
        const btn = (
          <Button
            key={action.key}
            type={action.buttonType ?? "link"}
            danger={action.danger}
            icon={action.icon}
            size="small"
          >
            {action.label}
          </Button>
        );

        if (action.confirm) {
          return (
            <Popconfirm
              key={action.key}
              title={action.confirm.title}
              description={action.confirm.description}
              okText={action.confirm.okText ?? "Yes"}
              cancelText={action.confirm.cancelText ?? "No"}
              okButtonProps={{ danger: action.danger }}
              onConfirm={() => action.confirm!.onConfirm(record)}
            >
              {btn}
            </Popconfirm>
          );
        }

        if (action.modal) {
          return React.cloneElement(btn, {
            key: action.key,
            onClick: () => onOpenModal({ action, record }),
          });
        }

        return React.cloneElement(btn, {
          key: action.key,
          onClick: () => action.onClick?.(record),
        });
      })}
    </BtnSpace>
  );
}

// -- main component ------------------------------------------------------------

export function AppGrid({
  columns,
  dataSource,
  url,
  loader,
  method = "GET",
  body,
  headers,
  actions,
  actionsTitle = "Actions",
  actionsWidth,
  actionsPosition = "end",
  serverPage = false,
  pageSize = 10,
  autoExpand = false,
  addButton,
  ...rest
}: AppGridProps) {
  const [{ data: urlData, loading, error, total }, dispatch] = useReducer(fetchReducer, {
    data: [],
    loading: false,
    error: null,
    total: 0,
  });

  const [current, setCurrent] = useState(1);
  // Own copy rather than reading the `pageSize` prop directly: the size
  // changer below has to update it, and a prop can't be written back into.
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);

  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const bodyRef = useRef(body);
  const headersRef = useRef(headers);
  const loaderRef = useRef(loader);
  useEffect(() => {
    bodyRef.current = body;
    headersRef.current = headers;
    loaderRef.current = loader;
  });

  useEffect(() => {
    if (dataSource) return;
    if (!url && !loaderRef.current) return;
    const controller = new AbortController();

    async function load() {
      dispatch({ type: "LOADING" });
      try {
        // Preferred path: a server action supplies the rows.
        if (loaderRef.current) {
          const raw = await loaderRef.current({ page: current, pageSize: currentPageSize });
          if (serverPage) {
            const items = Array.isArray(raw) ? raw : raw.data ?? [];
            const totalCount = Array.isArray(raw) ? items.length : raw.total ?? items.length;
            dispatch({ type: "SUCCESS", payload: items, total: totalCount });
          } else {
            dispatch({ type: "SUCCESS", payload: extractArray(raw) });
          }
          return;
        }

        const isPost = method === "POST";
        let fetchUrl = url!;

        // Add pagination params for server-side pagination
        if (serverPage) {
          const separator = fetchUrl.includes("?") ? "&" : "?";
          fetchUrl += `${separator}page=${current}&pageSize=${currentPageSize}`;
        }

        const res = await fetch(fetchUrl, {
          method,
          signal: controller.signal,
          headers: {
            ...(isPost ? { "Content-Type": "application/json" } : {}),
            ...headersRef.current,
          },
          body: isPost && bodyRef.current ? JSON.stringify(bodyRef.current) : undefined,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        const raw = await res.json();

        if (serverPage) {
          // Expect { data: [], total: number } for server-side pagination
          const items = Array.isArray(raw) ? raw : raw.data ?? raw.items ?? raw.rows ?? [];
          const totalCount = raw.total ?? items.length;
          dispatch({ type: "SUCCESS", payload: items, total: totalCount });
        } else {
          // Client-side: extract array and let Ant Design handle pagination
          dispatch({ type: "SUCCESS", payload: extractArray(raw) });
        }
      } catch (e) {
        if ((e as { name?: string }).name !== "AbortError") {
          dispatch({ type: "ERROR", payload: String(e) });
        }
      }
    }

    load();
    return () => controller.abort();
  }, [url, method, dataSource, serverPage, current, currentPageSize]);

  // A parent-supplied pageSize (e.g. remounting with a new filter key) should
  // win over whatever the size changer left selected on the previous mount —
  // otherwise a grid that remembered "50" from a prior visit would silently
  // ignore a caller that explicitly asked for 10.
  useEffect(() => {
    setCurrentPageSize(pageSize);
  }, [pageSize]);

  const activeData = dataSource ?? urlData;
  const dataColumns = buildColumns(columns, activeData[0]);

  // Auto-expand rows when autoExpand is true
  useEffect(() => {
    if (autoExpand && rest.child && activeData.length > 0) {
      const keys = activeData
        .map((record) => String(record.id ?? ""))
        .filter((key) => {
          const record = activeData.find((r) => String(r.id ?? "") === key);
          if (!record) return false;
          if (rest.child?.dataSourceKey) {
            return Array.isArray(record[rest.child.dataSourceKey]);
          }
          if (rest.child?.dataSource) {
            return Array.isArray(rest.child.dataSource);
          }
          const childDataSourceKey = Object.keys(record).find((k) => Array.isArray(record[k]));
          return !!(childDataSourceKey && Array.isArray(record[childDataSourceKey]));
        });
      setExpandedRowKeys(keys);
    } else if (!autoExpand) {
      setExpandedRowKeys([]);
    }
  }, [autoExpand, activeData, rest.child]);

  const actionCol: ColumnType<Record<string, unknown>> | null =
    actions && actions.length > 0
      ? {
        key: "__actions__",
        title: actionsTitle,
        width: actionsWidth ?? actions.length * 90,
        fixed: actionsPosition === "end" ? "right" : "left",
        render: (_, record) => (
          <ActionCell
            actions={actions}
            record={record}
            onOpenModal={setModalState}
          />
        ),
      }
      : null;

  const tableColumns: ColumnType<Record<string, unknown>>[] = actionCol
    ? actionsPosition === "start"
      ? [actionCol, ...dataColumns]
      : [...dataColumns, actionCol]
    : dataColumns;

  const { token } = theme.useToken();
  const closeModal = () => setModalState(null);

  const modalTitle =
    modalState?.action.modal?.title != null
      ? typeof modalState.action.modal.title === "function"
        ? modalState.action.modal.title(modalState.record)
        : modalState.action.modal.title
      : undefined;

  // add-button element
  const addBtn = addButton ? (
    addButton.modal ? (
      <AppModal
        title={addButton.modal.title}
        width={addButton.modal.width}
        trigger={
          <Button type="primary" icon={addButton.icon ?? <PlusOutlined />}>
            {addButton.label ?? "Add +"}
          </Button>
        }
      >
        {(close) => addButton.modal!.content(close)}
      </AppModal>
    ) : (
      <Button
        type="primary"
        icon={addButton.icon ?? <PlusOutlined />}
        onClick={addButton.onClick}
      >
        {addButton.label ?? "Add +"}
      </Button>
    )
  ) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {(error || addBtn) && (
        <Flex justify={error ? "space-between" : "flex-end"} align="center">
          {error && <span style={{ color: token.colorError, fontSize: 14 }}>{error}</span>}
          {addBtn}
        </Flex>
      )}
      <div
        style={{
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          overflow: "hidden",
          backgroundColor: token.colorBgContainer,
        }}
      >
        <Table
          columns={tableColumns}
          dataSource={activeData}
          loading={rest.loading ?? loading}
          rowKey={(row) => String(row.id ?? "")}
          scroll={actionCol ? { x: "max-content" } : undefined}
          bordered
          size="middle"
          pagination={
            serverPage
              ? {
                current,
                pageSize: currentPageSize,
                total: total ?? 0,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                showTotal: (t) => `${t} item${t === 1 ? "" : "s"}`,
                onChange: (page, size) => {
                  // A size change resets to page 1 — page 6 of 10-per-page is
                  // not page 6 of 50-per-page, and Table's own onChange fires
                  // both from the same control with no way to tell them apart.
                  if (size !== currentPageSize) {
                    setCurrentPageSize(size);
                    setCurrent(1);
                  } else {
                    setCurrent(page);
                  }
                },
              }
              : { pageSize: pageSize, showSizeChanger: true, showTotal: (t) => `${t} item${t === 1 ? "" : "s"}` }
          }
          expandable={
            rest.child
              ? {
                expandedRowRender: (record) => {
                  // Use the child prop as a nested grid, passing the array from the record
                  let childData: Record<string, unknown>[] = [];
                  if (rest.child?.dataSourceKey) {
                    childData = (record[rest.child.dataSourceKey] as Record<string, unknown>[] | undefined) ?? [];
                  } else if (rest.child?.dataSource) {
                    childData = rest.child.dataSource;
                  } else {
                    const childDataSourceKey = Object.keys(record).find(k => Array.isArray(record[k]));
                    childData = (childDataSourceKey ? record[childDataSourceKey] : []) as Record<string, unknown>[];
                  }
                  return (
                    <div style={{ margin: 0, padding: 0 }}>
                      <AppGrid {...rest.child} dataSource={childData} />
                    </div>
                  );
                },
                rowExpandable: (record) => {
                  if (rest.child?.dataSourceKey) {
                    return Array.isArray(record[rest.child.dataSourceKey]);
                  }
                  if (rest.child?.dataSource) {
                    return Array.isArray(rest.child.dataSource);
                  }
                  const childDataSourceKey = Object.keys(record).find(k => Array.isArray(record[k]));
                  return !!(childDataSourceKey && Array.isArray(record[childDataSourceKey]));
                },
                expandedRowKeys: autoExpand ? expandedRowKeys : undefined,
                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as React.Key[]),
              }
              : undefined
          }
          {...rest}
        />
      </div>
      {modalState?.action.modal && (
        <Modal
          open
          title={modalTitle}
          width={modalState.action.modal.width}
          onCancel={closeModal}
          footer={null}
          destroyOnHidden
        >
          {modalState.action.modal.content(modalState.record, closeModal)}
        </Modal>
      )}
    </div>
  );
}
